import React, { useState, useEffect } from "react";
import {
  Box,
  VStack,
  Button,
  FormControl,
  FormLabel,
  Container,
  useToast,
  Heading,
  Text,
  Select,
  useColorModeValue,
  Link,
  Divider,
  Flex,
  Icon,
  Progress,
  Input,
} from "@chakra-ui/react";
import { motion } from "framer-motion";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import { MdCloudUpload } from "react-icons/md";
import { getStorage, ref, uploadBytesResumable, getDownloadURL } from "firebase/storage";
import { doc, setDoc, serverTimestamp } from "firebase/firestore";
import { firestore } from "../../firebase/firebaseConfig";
import { useAuth } from "../../context/AuthProvider";
import { PeerxLogo } from "../../components/icons/Icons";
import { useTranslation } from "react-i18next";
import { fetchKYCStatus } from "../../services/kycService";

const eligibleDocuments = [
  "documentinfopage.document_1",
  "documentinfopage.document_2",
  "documentinfopage.document_3",
  "documentinfopage.document_4",
];

const MotionButton = motion(Button);

const DocumentsInfoPage = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const toast = useToast();
  const { user } = useAuth();

  const [formData, setFormData] = useState({
    idType: "",
    dateOfDelivery: "",
    dateOfExpiration: "",
    proofOfAddress: null,
  });

  const [loading, setLoading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  const textColor = useColorModeValue("brand.400", "white");
  const bgColor = useColorModeValue("brand.300", "gray.900");
  const cardBgColor = useColorModeValue("brand.100", "gray.700");
  const dropZoneBg = useColorModeValue("gray.100", "gray.600");

  useEffect(() => {
    const checkKYCStatus = async () => {
      if (!user?.uid) return;

      try {
        const kycStatus = await fetchKYCStatus(user.uid);

        if (kycStatus === "approved") {
          // If KYC is approved, redirect to dashboard
          navigate("/personal/dashboard");
          return;
        }

        if (!kycStatus || kycStatus === "rejected") {
          // If KYC is rejected or not found, stay on the page
          console.log("User KYC status is rejected or not found");
        }
      } catch (error) {
        console.error("Error fetching KYC status:", error);
        toast({
          title: t("signinpage.kyc_check_error_title"),
          description: t("signinpage.kyc_check_error_message"),
          status: "error",
          duration: 3000,
          isClosable: true,
        });
      }
    };

    checkKYCStatus();
  }, [user, navigate, toast, t]);

  // Handle Language Change
  const handleLanguageChange = (event) => {
    i18n.changeLanguage(event.target.value);
  };

  const handleChange = (e) => {
    const { name, value, type, files } = e.target;
    if (type === "file") {
      setFormData((prev) => ({
        ...prev,
        [name]: files[0],
      }));
      setUploadProgress(0);
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  const uploadFile = async (file) => {
    const storage = getStorage();
    const storageRef = ref(storage, `kyc_submissions/${user.uid}/proofOfAddress/${file.name}`);
    const uploadTask = uploadBytesResumable(storageRef, file);

    return new Promise((resolve, reject) => {
      uploadTask.on(
        "state_changed",
        (snapshot) => {
          const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
          setUploadProgress(progress);
        },
        (error) => {
          reject(error);
        },
        async () => {
          const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
          resolve(downloadURL);
        }
      );
    });
  };

  const handleSubmit = async () => {
    const { idType, dateOfDelivery, dateOfExpiration, proofOfAddress } = formData;

    if (!idType || !dateOfDelivery || !dateOfExpiration || !proofOfAddress) {
      toast({
        title: t("documentinfopage.missing_info_title"),
        description: t("documentinfopage.missing_info_message"),
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    try {
      setLoading(true);

      const fileURL = await uploadFile(proofOfAddress);

      await setDoc(doc(firestore, "kyc_submissions", user.uid), {
        userId: user.uid,
        idType,
        dateOfDelivery,
        dateOfExpiration,
        proofOfAddressURL: fileURL,
        status: "pending",
        uploadedBy: user.email,
        submittedAt: serverTimestamp(),
      });

      toast({
        title: t("documentinfopage.submit_success_title"),
        description: t("documentinfopage.submit_success_message"),
        status: "success",
        duration: 3000,
        isClosable: true,
      });

      navigate("/personal/kyc-upload");
    } catch (error) {
      toast({
        title: t("documentinfopage.submit_error_title"),
        description: error.message || t("documentinfopage.submit_error_message"),
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const renderDropZone = (label, file) => (
    <FormControl isRequired>
      <FormLabel color={textColor}>{label}</FormLabel>
      <Flex
        direction="column"
        align="center"
        justify="center"
        bg={dropZoneBg}
        border="2px dashed"
        borderColor={useColorModeValue("gray.300", "gray.500")}
        borderRadius="md"
        p={6}
        textAlign="center"
        _hover={{ bg: useColorModeValue("gray.200", "gray.700") }}
        cursor="pointer"
        onClick={() => document.getElementById("proofOfAddress").click()}
      >
        <Icon as={MdCloudUpload} boxSize={8} color="teal.500" mb={2} />
        <Text fontSize="sm" color="gray.500">
          {t("documentinfopage.dropzone_instructions")}
        </Text>
        {file && <Text mt={2} color={textColor} fontWeight="bold">{file.name}</Text>}
        <Input
          id="proofOfAddress"
          type="file"
          name="proofOfAddress"
          accept="image/jpeg, image/png, application/pdf"
          onChange={handleChange}
          hidden
        />
      </Flex>
      {file && <Progress value={uploadProgress} mt={2} size="sm" />}
    </FormControl>
  );

  return (
    <Box py={10} bg={bgColor} minH="100vh">
      {/* Language Switcher */}
      <Box position="fixed" top="4" right="4" zIndex="120" p={2}>
        <Select
          value={i18n.language}
          onChange={handleLanguageChange}
          width="120px"
          bg="white"
          color="black"
          size="sm"
        >
          <option value="en">English</option>
          <option value="fr">Français</option>
          <option value="es">Español</option>
        </Select>
      </Box>

      <Container maxW="md">
        <Heading mb="20px" size="2xl" fontFamily="Poppins" color="brand.200" fontWeight="bold" letterSpacing="wider" as={RouterLink} to="/">
          <PeerxLogo h="90px" w="160px" />
        </Heading>
        <Box
          as={motion.div}
          bg={cardBgColor}
          borderRadius="lg"
          shadow="lg"
          mt="20px"
          p={8}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <VStack spacing={6} align="stretch">
            <Box textAlign="center" mb={4}>
              <Heading size="lg" color="brand.300">
                {t("documentinfopage.form_title")}
              </Heading>
              <Text mt={2} color={useColorModeValue("gray.600", "gray.400")}>
                {t("documentinfopage.form_description")}
              </Text>
            </Box>

            <FormControl isRequired>
              <FormLabel color={textColor}>{t("documentinfopage.id_type_label")}</FormLabel>
              <Select
                name="idType"
                value={formData.idType}
                onChange={handleChange}
                placeholder={t("documentinfopage.id_type_placeholder")}
                bg={useColorModeValue("gray.50", "gray.600")}
              >
                <option value="gov_id">{t("documentinfopage.gov_id_option")}</option>
                <option value="passport">{t("documentinfopage.passport_option")}</option>
                <option value="driver_license">{t("documentinfopage.driver_license_option")}</option>
              </Select>
            </FormControl>

            <FormControl isRequired>
              <FormLabel color={textColor}>{t("documentinfopage.date_of_delivery_label")}</FormLabel>
              <Input
                name="dateOfDelivery"
                type="date"
                bg={useColorModeValue("gray.50", "gray.600")}
                value={formData.dateOfDelivery}
                onChange={handleChange}
              />
            </FormControl>

            <FormControl isRequired>
              <FormLabel color={textColor}>{t("documentinfopage.date_of_expiration_label")}</FormLabel>
              <Input
                name="dateOfExpiration"
                type="date"
                bg={useColorModeValue("gray.50", "gray.600")}
                value={formData.dateOfExpiration}
                onChange={handleChange}
              />
            </FormControl>

            {renderDropZone(t("documentinfopage.upload_proof_label"), formData.proofOfAddress)}

            <Text fontSize="sm" color="gray.500" mt={2}>
              {t("documentinfopage.eligible_documents_label")}
            </Text>
            <VStack spacing={1} align="start">
              {eligibleDocuments.map((key, index) => (
                <Text key={index} fontSize="sm" color="gray.500">
                  • {t(key)}
                </Text>
              ))}
            </VStack>
          </VStack>
        </Box>

        <MotionButton
          colorScheme="teal"
          w="full"
          mt={6}
          py={6}
          fontWeight="bold"
          onClick={handleSubmit}
          isLoading={loading}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          {t("documentinfopage.submit_button")}
        </MotionButton>

        <Divider my={6} />

        <Text fontSize="sm" color="gray.500" textAlign="center">
          {t("documentinfopage.need_help_message")}{" "}
          <Link color="brand.200" fontWeight="bold">
            {t("documentinfopage.contact_support")}
          </Link>
        </Text>
      </Container>
    </Box>
  );
};

export default DocumentsInfoPage;
